import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useAIAssistantStore } from "@/stores/ai-assistant";

// Re-export the public surface from the store so existing consumers that import
// from "@/contexts/AIAssistantContext" keep working unchanged.
export { useAIAssistant } from "@/stores/ai-assistant";
export type {
  Message,
  ConversationSession,
  WidgetMode,
  QuestionContext,
  UserContext,
} from "@/stores/ai-assistant";

interface AIAssistantProviderProps {
  children: ReactNode;
}

/**
 * Effects-only wrapper around the AI-assistant Zustand store. The store holds
 * all state and actions; this component supplies the React-context-bound pieces
 * the store cannot (auth and route).
 *
 * The time-on-page interval and the proactive-suggestion check that used to
 * live here are gone: `timeOnPage` never reached a prompt, and nothing ever
 * rendered a proactive suggestion, so both were a per-second tick and a state
 * machine with no consumer. The practice-question context — the one piece of
 * page context that does change a reply — is pushed in by
 * `PracticeChatColumn` via `updateContext`.
 */
export function AIAssistantProvider({ children }: AIAssistantProviderProps) {
  const location = useLocation();
  const { data: isAuthenticated, userId } = useAuth();
  // Persisted, so this is true on load only for a learner who left the panel
  // open — which is exactly when its history is worth fetching.
  const isOpen = useAIAssistantStore((state) => state.isOpen);

  // Sync auth snapshot + load DB sessions once authenticated *and* the panel is
  // up. This used to load on auth alone, from a provider mounted at the app
  // root: every page paid for the assistant's conversation list (and each
  // conversation's messages) for a panel nobody had opened. `loadSessionsForUser`
  // already guards with `hasLoadedFromDB`, so re-running per open is free.
  useEffect(() => {
    useAIAssistantStore.getState().setAuth(!!isAuthenticated, userId ?? null);
    if (isAuthenticated && userId && isOpen) {
      useAIAssistantStore.getState().loadSessionsForUser(userId);
    }
  }, [isAuthenticated, userId, isOpen]);

  // Keep the current route in the store so a widget request can tell the
  // assistant which page it was asked from.
  useEffect(() => {
    useAIAssistantStore.getState().setRoute(location.pathname);
  }, [location.pathname]);

  return <>{children}</>;
}
